/**
 * Iter XX.190 — Account deletion page.
 *
 * Public URL: /account/delete   (mounted in App.js)
 * Behaviour: signed-in user only. Confirms deletion via password
 *   (email accounts) or an explicit checkbox (Google / Emergent
 *   accounts). Calls POST /api/auth/delete-account. On success clears
 *   local auth state and redirects to home.
 *
 * The page is intentionally public in the router (no <Protected> wrap)
 * so unauthenticated visitors — including Google Play reviewers — can
 * reach it and read the instructions. If the visitor is not signed in
 * we simply render a sign-in prompt.
 */
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { clearToken } from "../lib/nativeTokenStorage";

const CONTACT_EMAIL = "support@gullyapp.com";

export default function AccountDelete() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmChecked, setConfirmChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const authMethod = (user?.auth_method || "email").toLowerCase();
  const isFederated = authMethod !== "email";

  const canSubmit =
    !submitting &&
    confirmChecked &&
    (isFederated ? true : password.length >= 1);

  const handleDelete = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setSubmitting(true);
    try {
      const body = isFederated
        ? { confirm: true }
        : { password, confirm: true };
      const { data } = await api.post("/auth/delete-account", body);
      setResult(data);
      // Clear client-side auth state (localStorage/Keychain on native).
      try { await clearToken(); } catch { /* noop */ }
      try { await setUser(null); } catch { /* noop */ }
      toast.success("Your account and all data have been deleted.");
      // After a short pause on the confirmation screen, send them home.
      setTimeout(() => { navigate("/", { replace: true }); }, 4000);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err.message || String(err);
      setError(status ? `${status}: ${detail}` : detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="grain min-h-screen bg-neutral-950 text-neutral-100"
      data-testid="account-delete-page"
    >
      <header className="border-b border-neutral-900">
        <div className="max-w-2xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/" className="font-display text-xl">gully</Link>
          <Link to="/privacy" className="text-sm text-neutral-400 hover:text-neutral-200">
            Privacy
          </Link>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <p className="label-eyebrow mb-3 text-neutral-500 text-xs uppercase tracking-wider">
          account · delete
        </p>
        <h1
          data-testid="account-delete-title"
          className="font-display font-light text-3xl sm:text-4xl tracking-tight mb-3"
        >
          Delete your Gully account
        </h1>
        <p className="text-neutral-400 mb-8 leading-relaxed">
          You can permanently delete your Gully account and everything
          we&apos;ve stored about you. This is immediate and cannot be
          undone.
        </p>

        {loading ? (
          <div
            data-testid="account-delete-loading"
            className="border border-neutral-800 rounded p-6 text-neutral-500 text-sm"
          >
            Checking your session…
          </div>
        ) : !user ? (
          <div
            data-testid="account-delete-signin-prompt"
            className="border border-neutral-800 rounded p-6 space-y-3"
          >
            <p className="text-neutral-300">
              You need to be signed in to delete your account.
            </p>
            <p className="text-neutral-500 text-sm">
              If you can&apos;t sign in (lost password, changed email, etc.),
              email us at{" "}
              <a href={`mailto:${CONTACT_EMAIL}?subject=Delete%20my%20Gully%20account`}
                 className="text-[#A7F3D0] underline">
                {CONTACT_EMAIL}
              </a>{" "}
              from the address on your account and we&apos;ll process it
              manually within seven days.
            </p>
            <div className="pt-2">
              <Link
                to="/login?return_to=%2Faccount%2Fdelete"
                data-testid="account-delete-signin-cta"
                className="inline-block px-4 py-2 rounded bg-neutral-800 hover:bg-neutral-700 text-sm"
              >
                Sign in to continue →
              </Link>
            </div>
          </div>
        ) : result ? (
          <div
            data-testid="account-delete-confirmation"
            className="border border-emerald-900 bg-emerald-950/30 text-emerald-100 rounded p-6"
          >
            <p className="font-display text-lg mb-2">Deleted.</p>
            <p className="text-sm text-emerald-200/90 leading-relaxed">
              We removed {result.total_documents_removed} record
              {result.total_documents_removed === 1 ? "" : "s"} tied to
              your account. You&apos;ll be returned to the home page in a
              moment. Thanks for spending time with Gully.
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleDelete}
            className="space-y-6"
            data-testid="account-delete-form"
          >
            <div className="border border-neutral-800 rounded p-5 space-y-2">
              <p className="text-sm text-neutral-300">
                Signed in as{" "}
                <span
                  data-testid="account-delete-user-email"
                  className="text-neutral-100 font-mono"
                >
                  {user.email}
                </span>
                .
              </p>
              <p className="text-sm text-neutral-500">
                What we will remove:
              </p>
              <ul className="text-sm text-neutral-400 space-y-1 mt-2">
                <li>· Your account, name, and any phone number you shared</li>
                <li>· Every conversation you had with Gully</li>
                <li>· Any reports Gully wrote for you</li>
                <li>· Your player memory and recurring-pattern history</li>
                <li>· Any videos you uploaded and their AI analyses</li>
                <li>· All internal event logs tied to your account</li>
              </ul>
              <p className="text-sm text-neutral-500 mt-3">
                What we cannot remove from here:
              </p>
              <ul className="text-sm text-neutral-400 space-y-1 mt-2">
                <li>
                  · Deleting your Gully account does not automatically
                  cancel an active Gully Plus subscription. If you
                  subscribed through the App Store or Google Play,
                  manage or cancel your subscription in your store
                  account (Settings → Apple ID → Subscriptions, or
                  Play Store → Subscriptions). If you subscribed on
                  the web, manage your web subscription separately.
                </li>
              </ul>
            </div>

            {isFederated ? (
              <div
                data-testid="account-delete-federated-notice"
                className="text-sm text-neutral-500"
              >
                You signed in with Google. Tick the confirmation below
                to proceed — we can&apos;t re-verify your Google account
                here, so the checkbox is your deliberate second
                confirmation.
              </div>
            ) : (
              <div className="space-y-2">
                <label
                  htmlFor="account-delete-password"
                  className="block text-sm text-neutral-300"
                >
                  Enter your password to confirm
                </label>
                <input
                  id="account-delete-password"
                  data-testid="account-delete-password-input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-neutral-700"
                  placeholder="•••••••"
                  required
                />
              </div>
            )}

            <label className="flex items-start gap-3 text-sm text-neutral-300 cursor-pointer">
              <input
                data-testid="account-delete-confirm-checkbox"
                type="checkbox"
                checked={confirmChecked}
                onChange={(e) => setConfirmChecked(e.target.checked)}
                className="mt-1"
              />
              <span>
                I understand this is <strong>immediate and permanent</strong>,
                and my conversations, reports, videos and memory
                cannot be restored afterwards.
              </span>
            </label>

            {error ? (
              <div
                data-testid="account-delete-error"
                className="border border-rose-900 bg-rose-950/30 text-rose-200 rounded p-3 text-sm"
              >
                {error}
              </div>
            ) : null}

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                type="submit"
                data-testid="account-delete-submit"
                disabled={!canSubmit}
                className={`px-4 py-2 rounded text-sm font-medium ${
                  canSubmit
                    ? "bg-rose-700 hover:bg-rose-600 text-white"
                    : "bg-neutral-800 text-neutral-500 cursor-not-allowed"
                }`}
              >
                {submitting ? "Deleting…" : "Permanently delete my account"}
              </button>
              <Link
                to="/chat"
                data-testid="account-delete-cancel"
                className="text-sm text-neutral-400 hover:text-neutral-200"
              >
                Cancel and go back
              </Link>
            </div>
          </form>
        )}

        <div className="mt-16 text-xs text-neutral-600">
          Full data-handling policy:{" "}
          <Link to="/privacy" className="underline text-[#A7F3D0]">
            gully.app/privacy
          </Link>
        </div>
      </main>
    </div>
  );
}
